import React, { useMemo, useState } from 'react';

/**
 * CalendarTimeline — a reusable calendar-grid layout shared between planning
 * views. Renders a time axis (periods + days) with one row per "thing"
 * (person, resource, ...), each row showing a set of horizontal bars that
 * span day-index ranges.
 *
 * Callers own:
 *  - building the `days`/`periods` arrays
 *  - producing each row's `bars`
 *  - how the left label column looks (`labelContent`)
 *  - optional per-row extras in the timeline area (e.g. "show suggestions")
 *
 * This component owns:
 *  - period-label header + striped background bands
 *  - weekend shading
 *  - "today" vertical line
 *  - bar positioning and hover tooltip
 */

export interface CalendarDay {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** Short display label (e.g. "Mon", "Mar 3"). Used to detect weekends by prefix. */
  label: string;
  /** Index of the period this day belongs to. */
  period_idx: number;
}

export interface CalendarPeriod {
  label: string;
}

/**
 * A single bar on a row. Variants are opaque strings; the caller provides a
 * style map in `variantStyles`. This keeps bar styling caller-controlled
 * while keeping the layout component variant-agnostic.
 */
export interface CalendarBar {
  key: string;
  label: string;
  startIdx: number;
  endIdx: number;
  variant: string;
  /** Optional small right-aligned text on the bar (e.g. effort or count). */
  meta?: string;
  /** Extra text shown in the tooltip after "label — meta". */
  tooltipSuffix?: string;
  onClick?: () => void;
  /** Inline style override (e.g. to offset vertically when multiple bars share a row). */
  topPx?: number;
}

export interface CalendarRow {
  key: string;
  /** Rendered in the left fixed-width label column. */
  labelContent: React.ReactNode;
  bars: CalendarBar[];
  /**
   * Optional per-row overlay rendered over the timeline area. Use for
   * row-specific affordances (e.g. PlanningGrid's "show suggestions" links).
   * Receives the period boundary metadata so callers can position elements.
   */
  timelineOverlay?: (ctx: TimelineOverlayContext) => React.ReactNode;
}

export interface TimelineOverlayContext {
  periodBoundaries: PeriodBoundary[];
  totalDays: number;
  days: CalendarDay[];
}

export interface VariantStyle {
  /** Tailwind classes for the default state. */
  idle: string;
  /** Tailwind classes when hovered. */
  hover: string;
}

interface PeriodBoundary {
  leftPct: number;
  widthPct: number;
  label: string;
  startIdx: number;
  endIdx: number;
}

interface CalendarTimelineProps {
  days: CalendarDay[];
  periods: CalendarPeriod[];
  rows: CalendarRow[];
  variantStyles: Record<string, VariantStyle>;
  labelColumnWidthPx?: number;
  /** Shown when `rows` or `days` is empty. */
  emptyMessage?: string;
  /** Minimum row height (px). The row grows beyond this for stacked bars. */
  minRowHeightPx?: number;
  /** Vertical pixel spacing between stacked bars in a row. */
  barStackSpacingPx?: number;
}

export const CalendarTimeline: React.FC<CalendarTimelineProps> = ({
  days,
  periods,
  rows,
  variantStyles,
  labelColumnWidthPx = 160,
  emptyMessage = 'No data.',
  minRowHeightPx = 44,
  barStackSpacingPx = 22,
}) => {
  const totalDays = days.length;

  const periodBoundaries = useMemo<PeriodBoundary[]>(() => {
    const boundaries: PeriodBoundary[] = [];
    for (let pi = 0; pi < periods.length; pi++) {
      const pDays = days.filter((d) => d.period_idx === pi);
      if (pDays.length === 0) continue;
      const firstIdx = days.indexOf(pDays[0]);
      const lastIdx = days.indexOf(pDays[pDays.length - 1]);
      boundaries.push({
        leftPct: (firstIdx / totalDays) * 100,
        widthPct: (pDays.length / totalDays) * 100,
        label: periods[pi].label,
        startIdx: firstIdx,
        endIdx: lastIdx,
      });
    }
    return boundaries;
  }, [days, periods, totalDays]);

  const today = new Date().toISOString().slice(0, 10);
  const todayIdx = days.findIndex((d) => d.date === today);
  const todayPct = todayIdx >= 0 ? ((todayIdx + 0.5) / totalDays) * 100 : -1;

  // Strip the current year from period labels when it matches — keeps the axis uncluttered.
  const currentYear = new Date().getFullYear().toString();
  const shortenedPeriodLabels = useMemo(
    () => periodBoundaries.map((pb) => pb.label.replace(` ${currentYear}`, '')),
    [periodBoundaries, currentYear],
  );

  if (rows.length === 0 || totalDays === 0) {
    return <div className="p-6 text-center text-cm-text-muted text-sm">{emptyMessage}</div>;
  }

  return (
    <div className="overflow-x-auto border border-cm-border-subtle rounded-lg">
      {/* Header — period labels only, no day ticks */}
      <div className="flex">
        <div
          className="shrink-0 bg-cm-bg-deep border-b border-cm-border-subtle"
          style={{ width: labelColumnWidthPx }}
        />
        <div className="flex-1 relative border-b border-cm-border-subtle min-w-[600px] h-7">
          {periodBoundaries.map((pb, i) => (
            <div
              key={i}
              className={`absolute top-0 h-full flex items-center justify-center text-[10px] font-medium text-cm-text-muted border-l border-cm-border-subtle ${
                i % 2 === 1 ? 'bg-cm-bg-deep/50' : ''
              }`}
              style={{ left: `${pb.leftPct}%`, width: `${pb.widthPct}%` }}
            >
              {shortenedPeriodLabels[i]}
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      {rows.map((row) => (
        <TimelineRow
          key={row.key}
          row={row}
          days={days}
          totalDays={totalDays}
          periodBoundaries={periodBoundaries}
          todayPct={todayPct}
          variantStyles={variantStyles}
          labelColumnWidthPx={labelColumnWidthPx}
          minRowHeightPx={minRowHeightPx}
          barStackSpacingPx={barStackSpacingPx}
        />
      ))}
    </div>
  );
};

// ── Internal row component ──────────────────────────────────────────

function TimelineRow({
  row,
  days,
  totalDays,
  periodBoundaries,
  todayPct,
  variantStyles,
  labelColumnWidthPx,
  minRowHeightPx,
  barStackSpacingPx,
}: {
  row: CalendarRow;
  days: CalendarDay[];
  totalDays: number;
  periodBoundaries: PeriodBoundary[];
  todayPct: number;
  variantStyles: Record<string, VariantStyle>;
  labelColumnWidthPx: number;
  minRowHeightPx: number;
  barStackSpacingPx: number;
}) {
  const [hoveredBarKey, setHoveredBarKey] = useState<string | null>(null);

  const barsWithTop = row.bars.map((b, i) => ({
    ...b,
    resolvedTopPx: b.topPx ?? 4 + i * barStackSpacingPx,
  }));
  const rowHeight = Math.max(minRowHeightPx, row.bars.length * barStackSpacingPx + 24);

  return (
    <div className="flex border-b border-cm-border-subtle">
      <div
        className="shrink-0 bg-cm-bg-deep px-3 py-2 flex items-center gap-2"
        style={{ width: labelColumnWidthPx }}
      >
        {row.labelContent}
      </div>

      <div className="flex-1 relative min-w-[600px] group/timeline" style={{ minHeight: rowHeight }}>
        {/* Period bands */}
        {periodBoundaries.map((pb, i) => (
          <div
            key={`band-${i}`}
            className={`absolute top-0 h-full pointer-events-none ${
              i % 2 === 1 ? 'bg-cm-bg-deep/30' : ''
            } ${i > 0 ? 'border-l border-cm-border-subtle/50' : ''}`}
            style={{ left: `${pb.leftPct}%`, width: `${pb.widthPct}%` }}
          />
        ))}

        {/* Weekend shading */}
        {days.map((d, i) => {
          if (!d.label?.startsWith('Sat') && !d.label?.startsWith('Sun')) return null;
          return (
            <div
              key={`we-${i}`}
              className="absolute top-0 h-full bg-gray-900/30 pointer-events-none"
              style={{
                left: `${(i / totalDays) * 100}%`,
                width: `${(1 / totalDays) * 100}%`,
              }}
            />
          );
        })}

        {/* Today line */}
        {todayPct >= 0 && (
          <div
            className="absolute top-0 h-full w-px bg-red-500/60 z-10 pointer-events-none"
            style={{ left: `${todayPct}%` }}
          />
        )}

        {/* Bars */}
        {barsWithTop.map((bar) => {
          const leftPct = (bar.startIdx / totalDays) * 100;
          const widthPct = ((bar.endIdx - bar.startIdx + 1) / totalDays) * 100;
          const isHovered = hoveredBarKey === bar.key;
          const style = variantStyles[bar.variant] ?? { idle: '', hover: '' };

          return (
            <React.Fragment key={bar.key}>
              <div
                className={`absolute h-[18px] rounded-sm px-1.5 flex items-center text-[10px] overflow-hidden z-[5] cursor-pointer transition-colors ${
                  isHovered ? style.hover : style.idle
                }`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%`, top: bar.resolvedTopPx }}
                onMouseEnter={() => setHoveredBarKey(bar.key)}
                onMouseLeave={() => setHoveredBarKey(null)}
                onClick={bar.onClick}
              >
                <span className="truncate font-medium">{bar.label}</span>
                {bar.meta && (
                  <span className="ml-auto shrink-0 opacity-70 tabular-nums pl-1">{bar.meta}</span>
                )}
              </div>
              {isHovered && (
                <div
                  className="absolute z-20 px-2 py-1 rounded bg-black/90 text-white text-[10px] shadow-lg whitespace-nowrap pointer-events-none"
                  style={{ left: `${leftPct}%`, top: bar.resolvedTopPx - 24 }}
                >
                  {bar.label}
                  {bar.meta && ` — ${bar.meta}`}
                  {bar.tooltipSuffix && ` ${bar.tooltipSuffix}`}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Caller-supplied overlay (e.g. PlanningGrid's "show suggestions" buttons) */}
        {row.timelineOverlay?.({ periodBoundaries, totalDays, days })}
      </div>
    </div>
  );
}
